import { DateTime, Settings, IANAZone } from "luxon";
import { Timestamp } from "firebase/firestore";

// Configure Luxon defaults for UK
Settings.defaultZone = "Europe/London"; // Handles both GMT and BST automatically
Settings.defaultLocale = "en-GB"; // Uses UK date formatting (DD/MM/YYYY)
Settings.throwOnInvalid = true; // Throw errors for invalid dates instead of returning invalid objects
Settings.defaultNumberingSystem = "latn"; // Use standard Latin numerals (0-9)
Settings.defaultOutputCalendar = "gregory"; // Use standard Gregorian calendar

/**
 * Converts a local DateTime or ISO date string to a UTC Firestore Timestamp.
 * Expected to be used on edit or create forms preparing data for storage.
 * @param {DateTime|string|Object} value - Luxon DateTime object, ISO date string, or serialized DateTime object
 * @returns {Timestamp|null} Firestore Timestamp in UTC or null if no value provided
 * @throws {Error} If value cannot be converted to a valid DateTime
 */
export const localToUtcTimestamp = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    throw new Error("Native Date objects are not supported. Please use Luxon DateTime.");
  }

  let dateTime;
  if (typeof value === "string") {
    dateTime = DateTime.fromISO(value);
    if (!dateTime.isValid) {
      throw new Error(`Invalid date string format: ${dateTime.invalidReason}`);
    }
  } else if (DateTime.isDateTime(value)) {
    dateTime = value;
  } else {
    // Try to reconstruct from serialized DateTime
    dateTime = reconstructDateTime(value);
    if (!dateTime) {
      throw new Error("Value must be a Luxon DateTime object, valid ISO date string, or serialized DateTime object.");
    }
  }

  if (!dateTime.isValid) {
    throw new Error(`Invalid DateTime: ${dateTime.invalidReason}`);
  }

  return Timestamp.fromDate(dateTime.toUTC().toJSDate());
};

/**
 * Converts a local date string to a UTC Firestore Timestamp.
 * Specifically handles date strings in the local timezone for "dates" coming from outside functions.
 * @param {string} value - Date string in local timezone (e.g., "2025-02-13")
 * @returns {Timestamp|null} Firestore Timestamp in UTC or null if no value provided
 * @throws {Error} If value is not a valid date string
 */
export const localStringToUtcTimestamp = (value) => {
  if (!value) return null;

  if (typeof value !== "string") {
    throw new Error("Value must be a string.");
  }

  // Parse the date string in the local timezone
  const dateTime = DateTime.fromISO(value, { zone: "local" });
  if (!dateTime.isValid) {
    throw new Error(`Invalid date string format: ${dateTime.invalidReason}`);
  }

  // Convert to UTC and create Timestamp
  return Timestamp.fromDate(dateTime.toUTC().toJSDate());
};

/**
 * Converts a UTC Firestore Timestamp to a local DateTime.
 * Expected to be used prepare stored data for edit forms.
 * @param {Timestamp|null} Firestore Timestamp in UTC or null if no value provided
 * @returns {DateTime} value - Luxon DateTime object
 * @throws {Error} If value is not a Timestamp
 */
export const utcTimestampToLocal = (value) => {
  if (!value) return null;

  if (!(value instanceof Timestamp)) {
    if (typeof value === "string") {
      console.warn("WARNING! Value stored as a string instead of utc timestamp.", value);
      return DateTime.fromISO(value);
    }

    throw new Error("Value must be a Firestore Timestamp.");
  }

  // Convert Firestore Timestamp to Luxon DateTime in local timezone
  return DateTime.fromJSDate(value.toDate());
};

/**
 * Converts a UTC Firestore Timestamp to a UTC DateTime.
 * Expected to be used when UTC time is needed without timezone conversion.
 * @param {Timestamp|null} value - Firestore Timestamp in UTC or null if no value provided
 * @returns {DateTime|null} Luxon DateTime object in UTC or null if no value provided
 * @throws {Error} If value is not a Firestore Timestamp
 */
export const utcTimestampToUtc = (value) => {
  if (!value) return null;

  if (!(value instanceof Timestamp)) {
    throw new Error("Value must be a Firestore Timestamp.");
  }

  // Convert Firestore Timestamp to Luxon DateTime in UTC
  return DateTime.fromJSDate(value.toDate(), { zone: "utc" });
};

/**
 * Formats a DateTime, ISO date string, or serialized DateTime to a formatted string using the specified format.
 * Expected to be used for labeling when the input is a Luxon DateTime object.
 * @param {DateTime|string|Object} value - Luxon DateTime object, ISO date string, or serialized DateTime object
 * @param {string} [format="dd/MM/yyyy HH:mm"] - Output format using Luxon format tokens
 * @returns {string} Formatted date string or empty string if no value provided
 * @throws {Error} If value cannot be converted to a valid DateTime
 */
export const dateTimeToString = (value, format = "dd/MM/yyyy HH:mm") => {
  if (!value) return "";

  if (value instanceof Date) {
    throw new Error("Native Date objects are not supported. Please use Luxon DateTime.");
  }

  let dateTime;
  if (typeof value === "string") {
    dateTime = DateTime.fromISO(value);
    if (!dateTime.isValid) {
      throw new Error(`Invalid date string format: ${dateTime.invalidReason}`);
    }
  } else if (DateTime.isDateTime(value)) {
    dateTime = value;
  } else {
    // Try to reconstruct from serialized DateTime
    dateTime = reconstructDateTime(value);
    if (!dateTime) {
      throw new Error("Value must be a Luxon DateTime object, valid ISO date string, or serialized DateTime object.");
    }
  }

  if (!dateTime.isValid) {
    throw new Error(`Invalid DateTime: ${dateTime.invalidReason}`);
  }

  return dateTime.toFormat(format);
};

/**
 * Converts a UTC Firestore Timestamp to a formatted local date string.
 * Expected to be used when displaying data from firestore in read mode.
 * @param {Timestamp} value - Firestore Timestamp in UTC
 * @param {string} [format="dd/MM/yyyy HH:mm"] - Output format using Luxon format tokens
 * @returns {string} Formatted date string in local timezone or empty string if no value provided
 * @throws {Error} If value is not a Firestore Timestamp
 */
/**
 * Converts a millisecond timestamp from a JSON Luxon date time object to a UTC Firestore Timestamp.
 * Expected to be used when receiving datetime data in millisecond format.
 * @param {number} milliseconds - Unix timestamp in milliseconds
 * @returns {Timestamp|null} Firestore Timestamp in UTC or null if no value provided
 * @throws {Error} If milliseconds is not a valid number
 */
export const millisecondsToUtcTimestamp = (milliseconds) => {
  if (!milliseconds) return null;

  if (typeof milliseconds !== "number" || isNaN(milliseconds)) {
    throw new Error("Value must be a valid number representing milliseconds.");
  }

  const dateTime = DateTime.fromMillis(milliseconds);
  if (!dateTime.isValid) {
    throw new Error(`Invalid millisecond timestamp: ${dateTime.invalidReason}`);
  }

  return localToUtcTimestamp(dateTime);
};

export const utcTimestampToLocalString = (value, format = "dd/MM/yyyy HH:mm") => {
  if (!value) return "";

  if (!(value instanceof Timestamp)) {
    throw new Error("Value must be a Firestore Timestamp.");
  }

  // Convert timestamp to local DateTime then format to string
  const localDateTime = utcTimestampToLocal(value);
  return localDateTime.toFormat(format);
};

/**
 * Converts any luxon date to midnight UTC timestamp, preserving only the date component
 * Ignores timezone and time information - only cares about year, month, day
 * @param {DateTime|string|Object} value - DateTime object, ISO date string, or serialized DateTime object
 * @returns {Timestamp} Firestore Timestamp at midnight UTC on the same date
 */
export const dateToMidnightUtcTimestamp = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    throw new Error("Native Date objects are not supported. Please use Luxon DateTime.");
  }

  let dateTime;
  if (typeof value === "string") {
    dateTime = DateTime.fromISO(value);
    if (!dateTime.isValid) {
      throw new Error(`Invalid date string format: ${dateTime.invalidReason}`);
    }
  } else if (DateTime.isDateTime(value)) {
    dateTime = value;
  } else {
    // Try to reconstruct from serialized DateTime (same pattern as other utils)
    dateTime = reconstructDateTime(value);
    if (!dateTime) {
      throw new Error("Value must be a Luxon DateTime object, valid ISO date string, or serialized DateTime object.");
    }
  }

  if (!dateTime.isValid) {
    throw new Error(`Invalid DateTime: ${dateTime.invalidReason}`);
  }

  // Extract date components and create midnight UTC timestamp
  const year = dateTime.year;
  const month = dateTime.month;
  const day = dateTime.day;

  const midnightUtc = DateTime.utc(year, month, day, 0, 0, 0, 0);
  return Timestamp.fromDate(midnightUtc.toJSDate());
};

/**
 * Reconstructs a Luxon DateTime from its serialized form.
 * @param {Object} value - Serialized DateTime object with ts, zone, and loc properties
 * @returns {DateTime} Reconstructed Luxon DateTime object
 * @throws {Error} If the serialized object is invalid or missing required properties
 */
const reconstructDateTime = (value) => {
  // Check if this looks like a serialized Luxon DateTime
  if (value && typeof value === "object" && typeof value.ts === "number" && typeof value.zone === "string" && value.loc) {
    try {
      // Create DateTime with exact timezone and locale settings
      return DateTime.fromMillis(value.ts, {
        zone: new IANAZone(value.zone),
        locale: value.loc.locale || "en-GB",
        numberingSystem: value.loc.numberingSystem || "latn",
        outputCalendar: value.loc.outputCalendar || "gregory",
      });
    } catch (error) {
      throw new Error(`Failed to reconstruct DateTime: ${error.message}`);
    }
  }
  return null;
};

/**
 * Calculates and returns a human-readable "time ago" string from a DateTime.
 * @param {DateTime|null} dateTime - Luxon DateTime object
 * @returns {string} Human-readable time ago string (e.g., "2 minutes ago", "3 hours ago")
 */
export const getTimeAgo = (dateTime) => {
  if (!dateTime) return "Never";

  if (!DateTime.isDateTime(dateTime)) {
    throw new Error("Value must be a Luxon DateTime object.");
  }

  if (!dateTime.isValid) {
    throw new Error(`Invalid DateTime: ${dateTime.invalidReason}`);
  }

  const now = DateTime.now();
  const diff = now.diff(dateTime, ["years", "months", "days", "hours", "minutes", "seconds"]);

  if (diff.years >= 1) {
    const years = Math.floor(diff.years);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }

  if (diff.months >= 1) {
    const months = Math.floor(diff.months);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  }

  if (diff.days >= 1) {
    const days = Math.floor(diff.days);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }

  if (diff.hours >= 1) {
    const hours = Math.floor(diff.hours);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }

  if (diff.minutes >= 1) {
    const minutes = Math.floor(diff.minutes);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }

  return "Just now";
};
